import { CircleMarker, Polyline, Tooltip } from "react-leaflet";
import type { ProjectionPoint } from "../map/types";

const MINUTE = 60;

/**
 * Where an aircraft will be over the next few minutes — the same computation the alert engine
 * uses (PLAN §5), fetched rather than reimplemented so the line you see is literally what a
 * notification would have reasoned about.
 *
 * Drawn as one segment per minute with falling opacity, and ticked at each minute. The fade is
 * the honest part: this extrapolates current velocity and turn rate, so the near end is a good
 * guess and the far end is a guess. A uniform line would imply the five-minute mark is as
 * trustworthy as the one-minute mark, which it is not.
 */
export default function ProjectionLayer({ points }: { points: ProjectionPoint[] }) {
  if (points.length < 2) return null;

  const minutes = Math.ceil(points[points.length - 1].t / MINUTE);
  const segments = Array.from({ length: minutes }, (_, i) => {
    const from = i * MINUTE;
    const to = (i + 1) * MINUTE;
    // Include the boundary point at both ends so consecutive segments join without a gap.
    return points.filter((p) => p.t >= from && p.t <= to);
  }).filter((seg) => seg.length >= 2);

  return (
    <>
      {segments.map((segment, index) => (
        <Polyline
          key={index}
          positions={segment.map((p) => [p.lat, p.lon] as [number, number])}
          pathOptions={{
            color: "#4da3ff",
            weight: 2,
            opacity: Math.max(0.12, 0.8 - index * 0.15),
            dashArray: "6 6",
          }}
          interactive={false}
        />
      ))}
      {points
        .filter((p) => p.t > 0 && p.t % MINUTE === 0)
        .map((p) => (
          <CircleMarker
            key={p.t}
            center={[p.lat, p.lon]}
            radius={3}
            pathOptions={{ color: "#4da3ff", fillColor: "#4da3ff", fillOpacity: 0.9, weight: 1 }}
            interactive={false}
          >
            <Tooltip permanent direction="top" offset={[0, -4]} className="tick-label">
              {p.t / MINUTE}′
            </Tooltip>
          </CircleMarker>
        ))}
    </>
  );
}
